<template>
  <mdb-container class="gp-has-header">
    <div v-show="views.list">
      <data-table ref="storeList" :add-handler="addStore"
                  :click-handler="showStore"
                  :columns="columns"
                  add-label="New Store"
                  data-source="/store/list"
                  page-title="Stores"
                  show-add-button/>
    </div>

    <div v-if="views.store">
      <div class="text-left">
        <mdb-btn icon="arrow-left" @click="toggleView('list')">Main</mdb-btn>
      </div>
      <mdb-card class="mb-4">
        <mdb-card-body class="mb-1 text-left">

          <form id="input-form" novalidate @submit.prevent="checkForm">
            <div class="form-row">
              <div class="col-md-6">
                <mdb-input v-model="editingStore.id" disabled label="ID"/>
              </div>
              <div class="col-md-6">
                <gp-select :dataObj="editingStore" :disabled="!editMode" :possible-values="currencies" :required="true"
                           label="Currency" propertyName="currency"/>
              </div>
            </div>
            <div class="form-row">
              <div class="col-md-6">
                <mdb-input v-model="editingStore.name" :disabled="!editMode" invalidFeedback="Please enter a name."
                           label="Name"
                           placeholder="Store Name" required
                           type="text"/>
              </div>
              <div class="col-md-6">
                <mdb-input v-model="editingStore.urlName" :disabled="!editMode"
                           invalidFeedback="Please enter a URL name."
                           label="URL Name"
                           placeholder="URL Name" required
                           type="text"/>
              </div>
            </div>
            <div class="md-form form-row">
              <mdb-input v-model="editingStore.taxRate" :disabled="!editMode"
                         label="Tax Rate"
                         placeholder="Rate" required
                         type="number" :step="0.01" :min="0.00"/>

            </div>
            <div class="md-form form-row">
              <mdb-btn v-if="editMode" id="invalidCheck" color="success" icon="save" type="submit" @click="save">Save
              </mdb-btn>
              <mdb-btn v-if="editMode" color="danger" icon="minus-circle" @click="cancelEdit">Cancel</mdb-btn>
              <mdb-btn v-if="!editMode" color="warning" floating icon="pencil" size="sm" tag="a" @click="editMode=true">
                Save
              </mdb-btn>
            </div>

          </form>
        </mdb-card-body>
      </mdb-card>
      <mdb-card v-if="editingStore.id && !editMode" class="mb-4">
        <mdb-card-header class="secondary-color text-white">
          <h5>Authorized Users
            <mdb-btn floating icon="plus-circle" size="sm" tag="a" @click="showUserModal=true"/>
          </h5>
        </mdb-card-header>
        <mdb-card-body class="text-left">
          <mdb-tbl responsive striped>
            <mdb-tbl-head>
              <tr class="text-center">
                <th>User</th>
                <th>Order Notifications?</th>
                <th>Manage Store?</th>
                <th>Manage Orders?</th>
                <th>&nbsp;</th>
              </tr>
            </mdb-tbl-head>
            <mdb-tbl-body>
              <tr v-for="user in editingStore.users" :key="user.id">
                <td>{{user.name}} ({{user.email}})</td>
                <td><mdb-input :id="'notify-orders-' + user.id" type="checkbox"
                               v-model="user.orderNotify" disabled/>
                </td>
                <td><mdb-input :id="'manage-store-' + user.id" type="checkbox"
                               v-model="user.manageStore" disabled/>
                </td>
                <td><mdb-input :id="'manage-orders-' + user.id" type="checkbox"
                               v-model="user.manageOrders" disabled/>
                </td>
                <td>
                  <mdb-btn @click="editUser(user)" size="sm" color="warning" rounded icon="pencil">Edit</mdb-btn>
                  <mdb-btn @click="deleteUser(user)" size="sm" color="danger" rounded icon="trash">Delete</mdb-btn>
                </td>
              </tr>
            </mdb-tbl-body>
          </mdb-tbl>
        </mdb-card-body>
      </mdb-card>
      <mdb-card v-if="editingStore.id && !editMode" class="mb-4">
        <mdb-card-header class="secondary-color text-white">
          <h5>Countries
            <mdb-btn floating icon="plus-circle" size="sm" tag="a" @click="showCountryModal=true"/>
          </h5>
        </mdb-card-header>
        <mdb-card-body class="text-left">

          <div>
            <span v-for="country in editingStore.countries" :key="country.id">
              <mdb-chip :alt="country.alpha3Code" close size="lg" waves @closeChip="removeCountry(country)">
                {{ country.name }}
              </mdb-chip>
            </span>
          </div>
        </mdb-card-body>
      </mdb-card>

      <mdb-card v-if="editingStore.id && !editMode" class="mb-4">
        <mdb-card-header class="secondary-color text-white">
          <h5>Products</h5>
        </mdb-card-header>
        <mdb-card-body class="text-left">
          <div>
            <mdb-btn icon="plus-circle" size="sm" rounded @click="showProductSearch=true">Add Product</mdb-btn>
            <mdb-btn icon="angle-double-down" size="sm" rounded @click="addAllProducts">Add All Products</mdb-btn>
          </div>
          <div>
            <mdb-tbl responsive striped>
              <mdb-tbl-head>
                <tr class="text-center">
                  <th>&nbsp;</th>
                  <th>Product</th>
                  <th>Current Inventory</th>
                  <th>Cost</th>
                  <th>Sell Price</th>
                  <th>Shipping</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                </tr>
              </mdb-tbl-head>
              <mdb-tbl-body>
                <tr v-for="product in editingStore.products" :key="product.id">
                  <td><img :src="product.defaultImage" width="75px"/></td>
                  <td>{{ product.name }}</td>
                  <td>
                    <mdb-input v-model="product.inStock" :disabled="!product.editing" :emptyValue="0" :step="0"
                               class="number-input"
                               type="number"/>
                  </td>
                  <td>
                    <mdb-input v-model="product.cost" :disabled="!product.editing" :emptyValue="0" :step="0.00"
                               class="number-input"
                               type="number"/>
                  </td>
                  <td>
                    <mdb-input v-model="product.price" :disabled="!product.editing" :emptyValue="0" :step="0.00"
                               class="number-input"
                               type="number"/>
                  </td>
                  <td>
                    <mdb-input v-model="product.shipping" :disabled="!product.editing" :emptyValue="0" :step="0.00"
                               class="number-input"
                               type="number"/>
                  </td>
                  <td>
                    <div class="form-check">
                    <input class="form-control form-check-input" :id="'visible-' + product.id"
                           type="checkbox"
                           :disabled="!product.editing"
                           v-model="product.visible"/>
                      <label :for="'visible-' + product.id">Visible</label>
                    </div>
                    <div class="form-check">
                      <input class="form-control form-check-input" :id="'nontaxable-' + product.id"
                             type="checkbox"
                             :disabled="!product.editing"
                             v-model="product.nonTaxable"/>
                      <label :for="'nontaxable-' + product.id">Non Taxable</label>
                    </div>
                  </td>
                  <td>
                    <mdb-btn size="sm" floating tag="a" icon="pencil" v-show="!product.editing" @click="editProduct(product)"/>
                    <mdb-btn size="sm" floating tag="a" icon="trash" color="danger" v-show="!product.editing" @click="deleteProduct(product)"/>
                    <mdb-btn size="sm" title="Save" floating tag="a" icon="save" color="success" v-show="product.editing" @click="saveProduct(product)"/>
                    <mdb-btn size="sm" title="Cancel" floating tag="a" icon="ban" color="warning" v-show="product.editing" @click="cancelProduct(product)"/>
                  </td>

                </tr>
              </mdb-tbl-body>
            </mdb-tbl>
          </div>
        </mdb-card-body>
      </mdb-card>

    </div>
    <mdb-modal v-if="showCountryModal" size="lg" @close="showCountryModal = false">
      <mdb-modal-header :close="true" class="primary-color text-white"></mdb-modal-header>
      <mdb-modal-body class="text-left">
        <div v-for="country in countries" :key="country.id">
          <mdb-btn icon="check-circle" rounded size="sm" @click="selectCountry(country)">Select</mdb-btn>
          {{ country.name }}
        </div>

      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="showUserModal" size="lg" @close="showUserModal = false">
      <mdb-modal-header :close="true" class="primary-color text-white"><h5>Pick A User to Add</h5></mdb-modal-header>
      <mdb-modal-body class="text-left">
        <data-table :clickHandler="pickedUser"
                    :columns="columns"
                    dataSource="/user/list"/>
      </mdb-modal-body>
    </mdb-modal>


    <mdb-modal v-show="showProductSearch" size="xl" @close="showProductSearch = false">
      <mdb-modal-header :close="true" class="primary-color text-white">Products</mdb-modal-header>
      <mdb-modal-body class="text-left">
        <data-table :click-handler="productSelected"
                    :columns="productColumns"
                    data-source="/product/list"/>

      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="editUserModal" size="xl" @close="editUserModal = false">
      <mdb-modal-header :close="true" class="primary-color text-white">User Authorization</mdb-modal-header>
      <mdb-modal-body class="text-left">
        <mdb-input type="text" disabled v-model="editingUser.name" label="User"/>
        <mdb-input id="order-notify" type="checkbox"
                   label="Order Notifications"
                   v-model="editingUser.orderNotify" />
        <mdb-input id="manage-orders" type="checkbox"
                   label="Manage Orders"
                   v-model="editingUser.manageOrders" />
        <mdb-input id="manage-store" type="checkbox"
                   label="Manage Store"
                   v-model="editingUser.manageStore" />
        <mdb-btn @click="saveUser" size="sm" rounded color="success" icon="save">Save</mdb-btn>
        <mdb-btn @click="editUserModal = false" size="sm" rounded color="warning" icon="minus-circle">Cancel</mdb-btn>

      </mdb-modal-body>
    </mdb-modal>

    <yes-no-modal v-if="yesNoModal" @yes="doDeleteProduct" @no="yesNoModal=false"/>
    <yes-no-modal v-if="yesNoModal2" @yes="doDeleteUser" @no="yesNoModal2=false"/>

  </mdb-container>
</template>

<script>
import dataTable from '../common/DataTable'
import api from '../../services/groupsPlaceApi.js'
import yesNoModal from "../common/YesNoModal";
import gpSelect from "../common/GPSelect";

import {
  mdbBadge,
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardHeader,
  mdbCardImage,
  mdbChip,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbRow,
  mdbTbl,
  mdbTblBody,
  mdbTblHead,
  mdbView
} from 'mdbvue';

export default {
  name: 'stores',
  components: {
    mdbContainer,
    mdbBtn,
    mdbChip,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbInput,
    mdbIcon,
    dataTable,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardImage,
    mdbRow,
    mdbCol,
    mdbMask,
    mdbView,
    mdbBadge,
    mdbTbl,
    mdbTblBody,
    mdbTblHead,
    yesNoModal,
    gpSelect
  },
  filters: {
    formatNumber: function (val) {
      return val.toLocaleString(undefined, {minimumFractionDigits: 2})
    }
  },
  computed: {
    userDetails: function () {
      return this.$store.state.userDetails
    }

  },
  data() {
    return {
      currentStore: null,
      editingStore: null,
      editMode: false,
      views: {list: true, store: false},
      deleteCandidate: null,
      showCountryModal: false,
      showUserModal: false,
      editUserModal: false,
      editingUser: null,
      newEmail: null,
      showProductSearch: false,
      countries: [],
      currencies: [],
      savedProduct: null,
      yesNoModal: false,
      yesNoModal2: false,
      columns: [
        {label: "ID", field: "id"},
        {label: "Name", field: "name"}
      ],
      productColumns: [
        {label: "", field: "defaultImage", type: "image", size: "85px"},
        {label: "Name", field: "name"},
        {label: "Category", field: "category"}

      ],
    }
  }
  ,
  mounted() {
    if (this.$route.params.id) {
      let row = {id: this.$route.params.id}
      this.showStore(row)
    }
    api.get("/public/countryList").then(
      (results) => {
        this.countries = results.data
        for (const c of this.countries) {
          let curr = {value: c.currencyCode, text: c.name + " " + c.currencySymbol + " " + c.currencyCode}
          this.currencies.push(curr)
        }
      },
      (error) => {
        this.$notify.error({message: error.response.data, timeOut: 20000})
      })
  }
  ,
  methods: {
    editUser(user) {
      this.editingUser = JSON.parse(JSON.stringify(user));
      this.editUserModal = true
    },
    pickedUser(user) {
      this.showUserModal = false
      this.editUserModal = true
      this.editingUser = { id: null, userId: user.id, name: user.name, manageStore: false, manageOrders: false, orderNotify: true }
      console.log("Picked user ",user)
    },
    deleteUser(user) {
      this.deleteCandidate = user
      this.yesNoModal2 = true
    },
    doDeleteUser() {
      this.yesNoModal2 = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/store/deleteUser/" + this.deleteCandidate.id).then(
        () => {
          let loc = this.editingStore.users.indexOf(this.deleteCandidate)
          this.editingStore.users.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deleteProduct(prod) {
      this.deleteCandidate = prod
      this.yesNoModal = true
    },
    doDeleteProduct() {
      this.yesNoModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/store/deleteProduct/" + this.deleteCandidate.id).then(
        () => {
          let loc = this.editingStore.products.indexOf(this.deleteCandidate)
          this.editingStore.products.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    editProduct(prod) {
      this.savedProduct = JSON.parse(JSON.stringify(prod));
      prod.editing = true
    },
    saveProduct(prod) {
      this.$store.dispatch("setExecuting", true)
      api.post("/store/saveProduct/", prod).then(
        (results) => {
          prod.editing = false
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    cancelProduct(prod) {
      prod.disabled = this.savedProduct.disabled
      prod.inStock = this.savedProduct.inStock
      prod.price = this.savedProduct.price
      prod.editing = false
      this.savedProduct = null
    },
    cancelEdit() {
      this.editMode = false
      if (!this.editingStore.id) {
        this.editingStore = null
        this.toggleView("list")
      }
    },
    productSelected(p) {
      this.showProductSearch = false
      api.post("/store/addProduct", {store: this.editingStore.id, product: p.id}).then(
        (results) => {
          for (let p of this.editingStore.products) {
            p.editing = false
          }
          this.editingStore.products.unshift(results.data)
          this.savedProduct = JSON.parse(JSON.stringify(results.data));
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    addAllProducts() {
      api.post("/store/addAllProducts", {store: this.editingStore.id}).then(
        (results) => {
          for (let p of results.data) {
            if (!this.editingStore.products) this.editingStore.products = [];
            this.editingStore.products.unshift(p);
          }
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    removeCountry(c) {
      this.$store.dispatch("setExecuting", true)
      api.delete("/store/deleteCountry/" + c.id).then(
        () => {
          let loc = this.editingStore.countries.indexOf(c)
          this.editingStore.countries.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    removeEmail(e) {
      this.$store.dispatch("setExecuting", true)
      api.delete("/store/deleteEmail/" + e.id).then(
        () => {
          let loc = this.editingStore.emails.indexOf(e)
          this.editingStore.emails.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    checkForm(event) {
      event.target.classList.add('was-validated');
      return false;
    },
    selectCountry(country) {
      this.showCountryModal = false
      let alreadyExists = false
      for (let c of this.editingStore.countries) {
        if (c.alpha3Code == country.alpha3Code) alreadyExists = true
      }
      if (alreadyExists) return
      this.$store.dispatch("setExecuting", true)
      api.post("/store/addCountry", {store: this.editingStore.id, country: country.id}).then(
        (results) => {
          this.editingStore.countries.push(results.data)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    saveUser() {
      this.editUserModal = false
      this.$store.dispatch("setExecuting", true)
      api.post("/store/user/" + this.editingStore.id, this.editingUser).then(
        (results) => {
          if (this.editingUser.id) {
            for (let u of this.editingStore.users) {
              if (u.id == this.editingUser.id) {
                u.orderNotify = results.data.orderNotify
                u.manageOrders = results.data.manageOrders
                u.manageStore = results.data.manageStore
              }
            }
          }
          else {
            this.editingStore.users.unshift(results.data)
          }
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },
    save() {
      if (!this.editingStore.name) return
      if (!this.editingStore.urlName) return
      if (!this.editingStore.currency) {
        this.$notify.error({message: "Currency Required", timeOut: 2000})
        return
      }

      this.$store.dispatch("setExecuting", true)
      api.post("/store/save", this.editingStore).then(
        (results) => {
          this.$notify.success({message: "Saved."})
          //this.editingStore = results.data
          if (!this.editingStore.id) this.editingStore.id = results.data.id
          this.editMode = false
          this.$refs.storeList.loadData()
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })

    },

    addStore() {
      this.editingStore = {
        id: null,
        name: "",
        urlName: "",
        currency: null,
        countries: []
      }
      this.toggleView("store")
      this.editMode = true
    }
    ,
    toggleView(view) {
      for (const v in this.views) {
        if (view == v) this.views[v] = true
        else this.views[v] = false
      }
      if (view != "list") {
        this.$nextTick().then(() => {
          window.scrollTo(0, this.listScrollPos)
        })
      }
    }
    ,
    showStore: function (row) {
      this.$store.dispatch("setExecuting", true)
      api.get("/store/show/" + row.id).then(
        (response) => {
          this.editingStore = response.data
          this.toggleView("store")
        },
        (error) => {
          this.showModal = false
          this.$notify.error({
                               message: "Error retrieving store. " + error.response.status,
                               timeOut: 5000,
                               position: 'top center'
                             })
        }
      ).then(() => {
        this.$store.dispatch("setExecuting", false)
      });
    }

  }

}
;
</script>
